/**
 * Markdown Renderer Component
 * Renders markdown content with syntax highlighting and GitHub Flavored Markdown support
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { clipboardApi } from '../api/clipboard';
import { toast } from 'react-hot-toast';
import UpgradePrompt from './UpgradePrompt';
import { useTouchDevice } from '../hooks/useTouchDevice';
import { SelectionPopup } from './SelectionPopup';
import { useClipboardStore } from '../stores/clipboardStore';
import {
  findTableForSelection,
  isTableSelection,
  extractTablesFromMarkdown,
  findTableInSelection,
  extractHeadingFromSelection,
} from '../utils/markdownUtils';
import 'highlight.js/styles/github-dark.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  conversationTitle?: string;
  onExportTable?: (tableMarkdown: string, title: string) => void;
}

export default function MarkdownRenderer({ content, className = '', conversationTitle, onExportTable }: MarkdownRendererProps) {
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ current: number; limit: number } | null>(null);
  const [flashingElement, setFlashingElement] = useState<string | null>(null);
  const isTouch = useTouchDevice();
  const { loadItems } = useClipboardStore();

  // Stable element ID counter (replaces Math.random() — deterministic per render)
  const idCounter = useRef(0);
  idCounter.current = 0; // Reset on each render so IDs are stable

  // Text selection for arbitrary text highlighting
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle long-press on mobile devices - save selection directly without popup
  const handleLongPress = async () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (text && text.length >= 3) {
      try {
        let contentToSave = text;

        // Check if selection is from a table - preserve markdown formatting
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);

          // Use the improved table detection
          const tableElement = findTableInSelection(range);

          if (tableElement) {
            const tableMarkdown = findTableForSelection(content, text);
            if (tableMarkdown) {
              // Check if there's a heading before the table
              const tableText = tableElement.textContent || '';
              const heading = extractHeadingFromSelection(text, tableText);

              if (heading) {
                const headingMatch = content.match(new RegExp(`(#{1,6}\\s*${heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').substring(0, 30)}[^\\n]*)`, 'i'));
                const mdHeading = headingMatch ? headingMatch[1] : `## ${heading}`;
                contentToSave = `${mdHeading}\n\n${tableMarkdown}`;
              } else {
                contentToSave = tableMarkdown;
              }
            }
          } else if (isTableSelection(text)) {
            const tableMarkdown = findTableForSelection(content, text);
            if (tableMarkdown) {
              contentToSave = tableMarkdown;
            }
          }
        }

        await clipboardApi.create({
          content_variants: { 'text/plain': contentToSave, markdown: contentToSave },
          title: conversationTitle,
          content_type: 'selection',
        });
        await loadItems();
        toast.success('Saved to Bridge');
        clearSelection();
      } catch (error: any) {
        // 402 (subscription lapsed) is reported and routed to billing by the API client interceptor
        if (error.response?.status !== 402) {
          toast.error('Failed to save selection');
        }
      }
    }
  };

  // Text selection popup disabled — interferes with native OS copy (Ctrl+C)
  const selectedText: string = '';
  const selectionRect = null as { top: number; left: number; right: number; bottom: number; width: number; height: number } | null;
  const clearSelection = () => {};
  const lockSelection = () => {};
  const unlockSelection = () => {};
  const preservedRange = null as Range | null;
  const highlightRects: DOMRect[] = [];

  const handleSaveToBridge = async (content: string, type: string, elementId?: string) => {
    // Trigger flash effect if elementId provided
    if (elementId) {
      setFlashingElement(elementId);
      setTimeout(() => setFlashingElement(null), 500);
    }

    try {
      await clipboardApi.create({
        content_variants: {
          plain: content,
          markdown: content,
        },
        title: `${type} from chat`,
        content_type: 'text',
        tags: ['chat', type, conversationTitle || 'conversation'],
      });

      // Reload clipboard items to update the sidebar count and list
      await loadItems();

      toast.success(`${type} saved to Bridge!`);
    } catch (error: any) {
      console.error('Error saving to Bridge:', error);

      // Check if it's a limit error
      if (error?.response?.status === 403 && error?.response?.data?.detail?.error_code === 'CLIPBOARD_LIMIT_REACHED') {
        const detail = error.response.data.detail;
        setLimitInfo({ current: detail.current, limit: detail.limit });
        setShowUpgradePrompt(true);
      } else {
        toast.error('Failed to save to Bridge');
      }
    }
  };

  // Handle saving selected text to bridge
  const handleSaveSelection = useCallback(async () => {
    if (!selectedText) return;

    try {
      let contentToSave = selectedText;

      // Check if selection is from a table - try to preserve markdown formatting
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // Use the improved table detection that handles heading + table selections
        const tableElement = findTableInSelection(range);

        if (tableElement) {
          // Selection contains or is within a table - try to get original markdown
          const tableMarkdown = findTableForSelection(content, selectedText);
          if (tableMarkdown) {
            // Check if there's a heading before the table in the selection
            const tableText = tableElement.textContent || '';
            const heading = extractHeadingFromSelection(selectedText, tableText);

            if (heading) {
              // Preserve the heading with the table
              // Try to find the markdown heading format
              const headingMatch = content.match(new RegExp(`(#{1,6}\\s*${heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').substring(0, 30)}[^\\n]*)`, 'i'));
              const mdHeading = headingMatch ? headingMatch[1] : `## ${heading}`;
              contentToSave = `${mdHeading}\n\n${tableMarkdown}`;
            } else {
              contentToSave = tableMarkdown;
            }
          }
        } else if (isTableSelection(selectedText)) {
          // Selection looks like table content - try to find matching table
          const tableMarkdown = findTableForSelection(content, selectedText);
          if (tableMarkdown) {
            contentToSave = tableMarkdown;
          }
        }
      }

      await handleSaveToBridge(contentToSave, 'Selection', undefined);
      clearSelection();
      // Clear browser selection
      window.getSelection()?.removeAllRanges();
    } catch (error) {
      console.error('Failed to save selection:', error);
    }
  }, [selectedText, content, clearSelection]);

  // Keyboard shortcut: Ctrl+Shift+B to save selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+B or Cmd+Shift+B on Mac
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'B') {
        e.preventDefault();

        // Check if we have a selection from the hook
        if (selectedText && selectionRect) {
          handleSaveSelection();
        } else {
          // Fallback: try to get any current browser selection
          const selection = window.getSelection();
          const text = selection?.toString().trim();

          if (text && text.length >= 3) {
            // Verify selection is within this markdown container
            const container = containerRef.current;
            if (container && selection?.anchorNode && container.contains(selection.anchorNode)) {
              let contentToSave = text;

              // Check if selection contains a table
              if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const tableElement = findTableInSelection(range);

                if (tableElement) {
                  const tableMarkdown = findTableForSelection(content, text);
                  if (tableMarkdown) {
                    // Check if there's a heading before the table
                    const tableText = tableElement.textContent || '';
                    const heading = extractHeadingFromSelection(text, tableText);

                    if (heading) {
                      const headingMatch = content.match(new RegExp(`(#{1,6}\\s*${heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').substring(0, 30)}[^\\n]*)`, 'i'));
                      const mdHeading = headingMatch ? headingMatch[1] : `## ${heading}`;
                      contentToSave = `${mdHeading}\n\n${tableMarkdown}`;
                    } else {
                      contentToSave = tableMarkdown;
                    }
                  }
                } else if (isTableSelection(text)) {
                  const tableMarkdown = findTableForSelection(content, text);
                  if (tableMarkdown) {
                    contentToSave = tableMarkdown;
                  }
                }
              }

              handleSaveToBridge(contentToSave, 'Selection', undefined)
                .then(() => {
                  window.getSelection()?.removeAllRanges();
                })
                .catch(err => console.error('Failed to save selection:', err));
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedText, selectionRect, handleSaveSelection, content]);

  // Helper function to extract content from sibling elements, preserving table markdown
  const extractSiblingContent = (sibling: Element): string => {
    // Check if this sibling contains a table
    const tableContainer = sibling.classList.contains('table-container') ? sibling : sibling.querySelector('.table-container');
    const table = tableContainer?.querySelector('table') || (sibling.tagName === 'TABLE' ? sibling : sibling.querySelector('table'));

    if (table) {
      // Extract the original markdown table from the content prop
      const tables = extractTablesFromMarkdown(content);
      if (tables.length > 0) {
        // Try to match this table by its text content
        const tableText = table.textContent || '';
        const normalizedTableText = tableText.replace(/\s+/g, ' ').trim().toLowerCase().substring(0, 50);

        for (const t of tables) {
          const tPlain = t.replace(/\|/g, ' ').replace(/-+/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
          if (tPlain.includes(normalizedTableText) || normalizedTableText.includes(tPlain.substring(0, 30))) {
            return t;
          }
        }
        // If no match found, return the first table (likely correct for single-table sections)
        return tables[0];
      }
    }

    // For non-table content, use textContent
    return sibling.textContent || '';
  };

  return (
    <div className={`markdown-content ${className}`} ref={containerRef}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Custom component styles with bridge buttons
          h1: ({ node, children, ...props }: any) => {
            const text = node?.children?.[0]?.value || children?.toString() || '';
            const elementId = `h1-${idCounter.current++}`;

            const handleClick = (e: React.MouseEvent) => {
              const h1Element = (e.target as HTMLElement).closest('h1');
              if (!h1Element) return;

              // Extract heading + all content until next heading
              let sectionContent = `# ${text}\n\n`;
              let sibling = h1Element.nextElementSibling;

              while (sibling && !sibling.tagName.match(/^H[1-6]$/)) {
                sectionContent += extractSiblingContent(sibling) + '\n\n';
                sibling = sibling.nextElementSibling;
              }

              handleSaveToBridge(sectionContent.trim(), text, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h1Element = button.closest('h1') as HTMLElement;
              if (!h1Element) return;

              // Highlight the heading itself
              h1Element.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';

              // Add highlight style to all siblings until next heading
              let sibling = h1Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-6]$/)) {
                sibling.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h1Element = button.closest('h1') as HTMLElement;
              if (!h1Element) return;

              // Remove highlight from heading
              h1Element.style.backgroundColor = '';

              // Remove highlight style from all siblings
              let sibling = h1Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-6]$/)) {
                sibling.style.backgroundColor = '';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            return (
              <h1 className={`text-2xl font-bold mb-4 mt-6 group flex items-center gap-2 rounded px-2 -mx-2 transition-colors duration-200 ${flashingElement === elementId ? 'animate-flash' : ''} hover:bg-bridge-hover`} {...props}>
                {children}
                <button
                  onClick={handleClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className={`inline-flex p-1 hover:bg-accent-blue/20 rounded transition-all ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Save section to Bridge"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                  </svg>
                </button>
              </h1>
            );
          },
          h2: ({ node, children, ...props }: any) => {
            const text = node?.children?.[0]?.value || children?.toString() || '';
            const elementId = `h2-${idCounter.current++}`;

            const handleClick = (e: React.MouseEvent) => {
              const h2Element = (e.target as HTMLElement).closest('h2');
              if (!h2Element) return;

              let sectionContent = `## ${text}\n\n`;
              let sibling = h2Element.nextElementSibling;

              while (sibling && !sibling.tagName.match(/^H[1-2]$/)) {
                sectionContent += extractSiblingContent(sibling) + '\n\n';
                sibling = sibling.nextElementSibling;
              }

              handleSaveToBridge(sectionContent.trim(), text, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h2Element = button.closest('h2') as HTMLElement;
              if (!h2Element) return;

              // Highlight the heading itself
              h2Element.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';

              // Add highlight style to all siblings until next h1 or h2
              let sibling = h2Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-2]$/)) {
                sibling.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h2Element = button.closest('h2') as HTMLElement;
              if (!h2Element) return;

              // Remove highlight from heading
              h2Element.style.backgroundColor = '';

              // Remove highlight style from all siblings
              let sibling = h2Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-2]$/)) {
                sibling.style.backgroundColor = '';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            return (
              <h2 className={`text-xl font-bold mb-3 mt-5 group flex items-center gap-2 rounded px-2 -mx-2 transition-colors duration-200 ${flashingElement === elementId ? 'animate-flash' : ''} hover:bg-bridge-hover`} {...props}>
                {children}
                <button
                  onClick={handleClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className={`inline-flex p-1 hover:bg-accent-blue/20 rounded transition-all ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Save section to Bridge"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                  </svg>
                </button>
              </h2>
            );
          },
          h3: ({ node, children, ...props }: any) => {
            const text = node?.children?.[0]?.value || children?.toString() || '';
            const elementId = `h3-${idCounter.current++}`;

            const handleClick = (e: React.MouseEvent) => {
              const h3Element = (e.target as HTMLElement).closest('h3');
              if (!h3Element) return;

              let sectionContent = `### ${text}\n\n`;
              let sibling = h3Element.nextElementSibling;

              while (sibling && !sibling.tagName.match(/^H[1-3]$/)) {
                sectionContent += extractSiblingContent(sibling) + '\n\n';
                sibling = sibling.nextElementSibling;
              }

              handleSaveToBridge(sectionContent.trim(), text, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h3Element = button.closest('h3') as HTMLElement;
              if (!h3Element) return;

              // Highlight the heading itself
              h3Element.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';

              let sibling = h3Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-3]$/)) {
                sibling.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h3Element = button.closest('h3') as HTMLElement;
              if (!h3Element) return;

              // Remove highlight from heading
              h3Element.style.backgroundColor = '';

              let sibling = h3Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-3]$/)) {
                sibling.style.backgroundColor = '';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            return (
              <h3 className={`text-lg font-bold mb-2 mt-4 group flex items-center gap-2 rounded px-2 -mx-2 transition-colors duration-200 ${flashingElement === elementId ? 'animate-flash' : ''} hover:bg-bridge-hover`} {...props}>
                {children}
                <button
                  onClick={handleClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className={`inline-flex p-1 hover:bg-accent-blue/20 rounded transition-all ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Save section to Bridge"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                  </svg>
                </button>
              </h3>
            );
          },
          h4: ({ node, children, ...props }: any) => {
            const text = node?.children?.[0]?.value || children?.toString() || '';
            const elementId = `h4-${idCounter.current++}`;

            const handleClick = (e: React.MouseEvent) => {
              const h4Element = (e.target as HTMLElement).closest('h4');
              if (!h4Element) return;

              let sectionContent = `#### ${text}\n\n`;
              let sibling = h4Element.nextElementSibling;

              while (sibling && !sibling.tagName.match(/^H[1-4]$/)) {
                sectionContent += extractSiblingContent(sibling) + '\n\n';
                sibling = sibling.nextElementSibling;
              }

              handleSaveToBridge(sectionContent.trim(), text, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h4Element = button.closest('h4') as HTMLElement;
              if (!h4Element) return;

              // Highlight the heading itself
              h4Element.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';

              let sibling = h4Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-4]$/)) {
                sibling.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const h4Element = button.closest('h4') as HTMLElement;
              if (!h4Element) return;

              // Remove highlight from heading
              h4Element.style.backgroundColor = '';

              let sibling = h4Element.nextElementSibling as HTMLElement;
              while (sibling && !sibling.tagName.match(/^H[1-4]$/)) {
                sibling.style.backgroundColor = '';
                sibling = sibling.nextElementSibling as HTMLElement;
              }
            };

            return (
              <h4 className={`text-base font-bold mb-2 mt-3 group flex items-center gap-2 rounded px-2 -mx-2 transition-colors duration-200 ${flashingElement === elementId ? 'animate-flash' : ''} hover:bg-bridge-hover`} {...props}>
                {children}
                <button
                  onClick={handleClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className={`inline-flex p-1 hover:bg-accent-blue/20 rounded transition-all ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Save section to Bridge"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                  </svg>
                </button>
              </h4>
            );
          },
          h5: ({ node, children, ...props }: any) => {
            const text = node?.children?.[0]?.value || children?.toString() || '';
            const elementId = `h5-${idCounter.current++}`;

            const handleClick = (e: React.MouseEvent) => {
              const el = (e.target as HTMLElement).closest('h5');
              if (!el) return;
              let sectionContent = `##### ${text}\n\n`;
              let sibling = el.nextElementSibling;
              while (sibling && !sibling.tagName.match(/^H[1-5]$/)) {
                sectionContent += (sibling.textContent || '') + '\n\n';
                sibling = sibling.nextElementSibling;
              }
              handleSaveToBridge(sectionContent.trim(), text, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const el = (e.target as HTMLElement).closest('h5');
              if (el) {
                let sibling = el.nextElementSibling;
                while (sibling && !sibling.tagName.match(/^H[1-5]$/)) {
                  (sibling as HTMLElement).style.backgroundColor = 'rgba(var(--color-accent-blue), 0.05)';
                  sibling = sibling.nextElementSibling;
                }
              }
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const el = (e.target as HTMLElement).closest('h5');
              if (el) {
                let sibling = el.nextElementSibling;
                while (sibling && !sibling.tagName.match(/^H[1-5]$/)) {
                  (sibling as HTMLElement).style.backgroundColor = '';
                  sibling = sibling.nextElementSibling;
                }
              }
            };

            return (
              <h5 id={elementId} className={`text-sm font-bold mb-2 mt-3 text-dark-text-muted uppercase tracking-wide group flex items-center gap-2 hover:bg-bridge-hover ${flashingElement === elementId ? 'animate-flash' : ''}`} {...props}>
                {children}
                <button
                  onClick={handleClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className={`inline-flex p-1 hover:bg-accent-blue/20 rounded transition-all ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Save section to Bridge"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                  </svg>
                </button>
              </h5>
            );
          },
          h6: ({ node, children, ...props }: any) => {
            const text = node?.children?.[0]?.value || children?.toString() || '';
            const elementId = `h6-${idCounter.current++}`;

            const handleClick = (e: React.MouseEvent) => {
              const el = (e.target as HTMLElement).closest('h6');
              if (!el) return;
              let sectionContent = `###### ${text}\n\n`;
              let sibling = el.nextElementSibling;
              while (sibling && !sibling.tagName.match(/^H[1-6]$/)) {
                sectionContent += (sibling.textContent || '') + '\n\n';
                sibling = sibling.nextElementSibling;
              }
              handleSaveToBridge(sectionContent.trim(), text, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const el = (e.target as HTMLElement).closest('h6');
              if (el) {
                let sibling = el.nextElementSibling;
                while (sibling && !sibling.tagName.match(/^H[1-6]$/)) {
                  (sibling as HTMLElement).style.backgroundColor = 'rgba(var(--color-accent-blue), 0.05)';
                  sibling = sibling.nextElementSibling;
                }
              }
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const el = (e.target as HTMLElement).closest('h6');
              if (el) {
                let sibling = el.nextElementSibling;
                while (sibling && !sibling.tagName.match(/^H[1-6]$/)) {
                  (sibling as HTMLElement).style.backgroundColor = '';
                  sibling = sibling.nextElementSibling;
                }
              }
            };

            return (
              <h6 id={elementId} className={`text-xs font-semibold mb-2 mt-2 text-dark-text-muted uppercase tracking-wider group flex items-center gap-2 hover:bg-bridge-hover ${flashingElement === elementId ? 'animate-flash' : ''}`} {...props}>
                {children}
                <button
                  onClick={handleClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className={`inline-flex p-1 hover:bg-accent-blue/20 rounded transition-all ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Save section to Bridge"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                  </svg>
                </button>
              </h6>
            );
          },
          p: ({ node, ...props }) => <p className="mb-2 leading-relaxed transition-colors duration-200" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc list-outside pl-6 space-y-1 mb-3 rounded transition-colors duration-200" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal list-outside pl-6 space-y-1 mb-3 rounded transition-colors duration-200" {...props} />,
          li: ({ node, children, ...props }: any) => {
            // Check if this is a task list item by looking for checkbox input in children
            const childArray = Array.isArray(children) ? children : [children];
            const hasCheckbox = childArray.some((child: any) =>
              child?.type === 'input' && child?.props?.type === 'checkbox'
            );

            if (hasCheckbox) {
              // Task list item - render with custom checkbox styling
              const isChecked = childArray.find((child: any) =>
                child?.type === 'input' && child?.props?.type === 'checkbox'
              )?.props?.checked || false;

              // Filter out the original checkbox and render our custom one
              const filteredChildren = childArray.filter((child: any) =>
                !(child?.type === 'input' && child?.props?.type === 'checkbox')
              );

              return (
                <li className="ml-4 flex items-start gap-2 list-none" {...props}>
                  <span className={`inline-flex items-center justify-center w-4 h-4 mt-1 rounded border flex-shrink-0 ${
                    isChecked
                      ? 'bg-accent-blue border-accent-blue'
                      : 'border-dark-border bg-dark-bg'
                  }`}>
                    {isChecked && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className={isChecked ? 'line-through text-dark-text-muted' : ''}>
                    {filteredChildren}
                  </span>
                </li>
              );
            }

            return <li className="ml-4" {...props}>{children}</li>;
          },
          blockquote: ({ node, children, ...props }: any) => {
            // Check for callout patterns at the start of the blockquote
            const childArray = Array.isArray(children) ? children : [children];
            const firstParagraph = childArray.find((child: any) => child?.type === 'p');
            const textContent = firstParagraph?.props?.children;

            // Extract text for pattern matching
            let textToCheck = '';
            if (typeof textContent === 'string') {
              textToCheck = textContent;
            } else if (Array.isArray(textContent)) {
              // Look for bold element followed by text
              const boldElement = textContent.find((c: any) => c?.type === 'strong');
              if (boldElement) {
                textToCheck = typeof boldElement.props?.children === 'string'
                  ? boldElement.props.children
                  : '';
              }
            }

            // Callout type detection
            const calloutPatterns: { [key: string]: { icon: JSX.Element; borderColor: string; bgColor: string; textColor: string } } = {
              'note': {
                icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                borderColor: 'border-accent-blue',
                bgColor: 'bg-accent-blue/10',
                textColor: 'text-accent-blue'
              },
              'warning': {
                icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
                borderColor: 'border-yellow-500',
                bgColor: 'bg-yellow-500/10',
                textColor: 'text-yellow-500'
              },
              'important': {
                icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                borderColor: 'border-accent-orange',
                bgColor: 'bg-accent-orange/10',
                textColor: 'text-accent-orange'
              },
              'tip': {
                icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
                borderColor: 'border-green-500',
                bgColor: 'bg-green-500/10',
                textColor: 'text-green-500'
              },
              'caution': {
                icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
                borderColor: 'border-red-500',
                bgColor: 'bg-red-500/10',
                textColor: 'text-red-500'
              }
            };

            // Check for callout pattern (case-insensitive)
            const lowerText = textToCheck.toLowerCase();
            let calloutType: string | null = null;

            for (const type of Object.keys(calloutPatterns)) {
              if (lowerText.startsWith(type) || lowerText === type + ':') {
                calloutType = type;
                break;
              }
            }

            if (calloutType) {
              const callout = calloutPatterns[calloutType];
              return (
                <blockquote className={`border-l-4 ${callout.borderColor} ${callout.bgColor} pl-4 pr-4 py-3 my-4 rounded-r-lg`} {...props}>
                  <div className="flex items-start gap-3">
                    <span className={`${callout.textColor} flex-shrink-0 mt-0.5`}>{callout.icon}</span>
                    <div className="flex-1 text-dark-text">{children}</div>
                  </div>
                </blockquote>
              );
            }

            // Default blockquote styling
            return (
              <blockquote className="border-l-4 border-accent-blue pl-4 italic my-4 text-dark-text-muted" {...props}>
                {children}
              </blockquote>
            );
          },
          code: ({ node, className, children, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !String(className || '').startsWith('language-');
            return isInline ? (
              <code className="bg-dark-bg px-1.5 py-0.5 rounded text-sm font-mono text-accent-blue" {...props}>
                {children}
              </code>
            ) : (
              <code className={`${className} block`} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ node, children, ...props }: any) => {
            const elementId = `code-${idCounter.current++}`;

            // Extract language from code block
            const codeElement = node?.children?.[0];
            const className = codeElement?.properties?.className?.[0] || '';
            const language = className.replace('language-', '') || 'code';
            const isGraph = ['mermaid', 'graphviz', 'dot'].includes(language.toLowerCase());

            const handleClick = (e: React.MouseEvent) => {
              const codeContainer = (e.target as HTMLElement).closest('.code-container');
              if (!codeContainer) return;

              const preElement = codeContainer.querySelector('pre');
              if (!preElement) return;

              // Find preceding heading as title
              let title = isGraph ? 'Graph' : 'Code';
              let prevElement = codeContainer.previousElementSibling;
              while (prevElement) {
                if (prevElement.tagName.match(/^H[1-6]$/)) {
                  title = prevElement.textContent || title;
                  break;
                }
                prevElement = prevElement.previousElementSibling;
              }

              // Extract code content
              const codeText = preElement.textContent || '';
              const content = `### ${title}\n\n\`\`\`${language}\n${codeText}\n\`\`\``;

              handleSaveToBridge(content.trim(), title, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const codeContainer = button.closest('.code-container') as HTMLElement;
              if (!codeContainer) return;

              codeContainer.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const codeContainer = button.closest('.code-container') as HTMLElement;
              if (!codeContainer) return;

              codeContainer.style.backgroundColor = '';
            };

            return (
              <div className={`code-container relative mb-4 rounded-lg transition-colors duration-200 group ${flashingElement === elementId ? 'animate-flash' : ''} hover:bg-bridge-hover`}>
                {/* Right scroll indicator gradient for mobile */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-dark-bg via-dark-bg/50 to-transparent pointer-events-none rounded-r-lg md:hidden z-10"></div>
                <pre className="bg-dark-bg rounded-lg p-4 overflow-x-auto border border-dark-border text-xs sm:text-sm -webkit-overflow-scrolling-touch" {...props}>{children}</pre>
                <button
                  onClick={handleClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className={`absolute top-2 right-2 p-1.5 hover:bg-accent-blue/20 rounded transition-all bg-dark-card/80 backdrop-blur-sm z-20 ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title={`Save ${isGraph ? 'graph' : 'code'} to Bridge`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                  </svg>
                </button>
              </div>
            );
          },
          a: ({ node, ...props }) => (
            <a className="text-accent-blue hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
          ),
          del: ({ node, ...props }) => (
            <del className="text-dark-text-muted line-through decoration-red-400/60 decoration-2" {...props} />
          ),
          table: ({ node, ...props }: any) => {
            const elementId = `table-${idCounter.current++}`;

            const handleClick = (e: React.MouseEvent) => {
              const tableContainer = (e.target as HTMLElement).closest('.table-container');
              if (!tableContainer) return;

              const tableElement = tableContainer.querySelector('table');
              if (!tableElement) return;

              // Find preceding heading as title
              let title = 'Table';
              let prevElement = tableContainer.previousElementSibling;
              while (prevElement) {
                if (prevElement.tagName.match(/^H[1-6]$/)) {
                  title = prevElement.textContent || 'Table';
                  break;
                }
                prevElement = prevElement.previousElementSibling;
              }

              // Extract table content as markdown - use original markdown to preserve formatting
              const tables = extractTablesFromMarkdown(content);
              // If multiple tables, try to find the right one based on table text content
              let tableMarkdown = tables[0] || '';

              if (tables.length > 1) {
                const tableText = tableElement.textContent || '';
                const normalizedTableText = tableText.replace(/\s+/g, ' ').trim().toLowerCase();
                for (const t of tables) {
                  const tPlain = t.replace(/\|/g, ' ').replace(/-+/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
                  if (tPlain.includes(normalizedTableText.substring(0, 30)) ||
                      normalizedTableText.includes(tPlain.substring(0, 30))) {
                    tableMarkdown = t;
                    break;
                  }
                }
              }

              const contentToSave = tableMarkdown
                ? `### ${title}\n\n${tableMarkdown}`
                : `### ${title}\n\n${tableElement.textContent || ''}`;

              handleSaveToBridge(contentToSave.trim(), title, elementId);
            };

            const handleMouseEnter = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const tableContainer = button.closest('.table-container') as HTMLElement;
              if (!tableContainer) return;

              tableContainer.style.backgroundColor = 'rgba(238, 108, 76, 0.15)';
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button');
              if (!button) return;

              const tableContainer = button.closest('.table-container') as HTMLElement;
              if (!tableContainer) return;

              tableContainer.style.backgroundColor = '';
            };

            const handleExportClick = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (!onExportTable) return;

              const tableContainer = (e.target as HTMLElement).closest('.table-container');
              if (!tableContainer) return;

              const tableElement = tableContainer.querySelector('table');
              if (!tableElement) return;

              // Find preceding heading as title
              let title = 'Table';
              let prevElement = tableContainer.previousElementSibling;
              while (prevElement) {
                if (prevElement.tagName.match(/^H[1-6]$/)) {
                  title = prevElement.textContent || 'Table';
                  break;
                }
                prevElement = prevElement.previousElementSibling;
              }

              // Extract table content as markdown
              const tables = extractTablesFromMarkdown(content);
              let tableMarkdown = tables[0] || '';

              if (tables.length > 1) {
                const tableText = tableElement.textContent || '';
                const normalizedTableText = tableText.replace(/\s+/g, ' ').trim().toLowerCase();
                for (const t of tables) {
                  const tPlain = t.replace(/\|/g, ' ').replace(/-+/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
                  if (tPlain.includes(normalizedTableText.substring(0, 30)) ||
                      normalizedTableText.includes(tPlain.substring(0, 30))) {
                    tableMarkdown = t;
                    break;
                  }
                }
              }

              const contentToExport = tableMarkdown
                ? `### ${title}\n\n${tableMarkdown}`
                : `### ${title}\n\n${tableElement.textContent || ''}`;

              onExportTable(contentToExport.trim(), title);
            };

            return (
              <div className={`table-container overflow-x-auto mb-4 rounded-lg transition-colors duration-200 px-2 -mx-2 group relative ${flashingElement === elementId ? 'animate-flash' : ''} hover:bg-bridge-hover`}>
                <table className="border border-dark-border" {...props} />
                {/* Action buttons container */}
                <div className={`absolute top-2 right-2 flex gap-1 ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                  {/* Save to Bridge button */}
                  <button
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className="p-1.5 hover:bg-accent-blue/20 rounded transition-all bg-dark-card/80 backdrop-blur-sm"
                    title="Save table to Bridge"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-accent-blue">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                    </svg>
                  </button>
                  {/* Export to File button */}
                  {onExportTable && (
                    <button
                      onClick={handleExportClick}
                      className="p-1.5 hover:bg-accent-orange/20 rounded transition-all bg-dark-card/80 backdrop-blur-sm"
                      title="Export table to file (DOCX, XLSX)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-accent-orange">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          },
          thead: ({ node, ...props }) => <thead className="bg-dark-bg" {...props} />,
          th: ({ node, ...props }) => <th className="border border-dark-border px-4 py-2 text-left" {...props} />,
          td: ({ node, ...props }) => <td className="border border-dark-border px-4 py-2" {...props} />,
          hr: ({ node, ...props }) => <hr className="my-6 border-dark-border" {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && limitInfo && (
        <UpgradePrompt
          isOpen={showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(false)}
          limitType="clipboard"
          currentCount={limitInfo.current}
          limit={limitInfo.limit}
        />
      )}

      {/* DISABLED: Selection Popup for arbitrary text highlighting - Desktop only (mobile uses long-press)
          Interferes with native OS copy (Ctrl+C). Re-enable once coexistence issue is resolved.
      {!isTouch && (
        <SelectionPopup
          text={selectedText}
          position={selectionRect || { top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 }}
          onSave={handleSaveSelection}
          onDismiss={clearSelection}
          onLock={lockSelection}
          onUnlock={unlockSelection}
          range={preservedRange}
          visible={!!(selectedText && selectionRect)}
          highlightRects={highlightRects}
        />
      )}
      */}
    </div>
  );
}
